"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  FileText,
  Users,
  FolderKanban,
  FileStack,
  Settings,
  ChevronLeft,
  LayoutDashboard,
  Shield,
  MessageSquare,
  BookOpen,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { BRANDING } from "@/config/branding";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useUIStore } from "@/stores/ui-store";
import { useAuthStore } from "@/stores/auth-store";
import { SpaceSidebar } from "./SpaceSidebar";

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
}

const mainNavItems: NavItem[] = [
  {
    title: "Dashboard",
    href: "/",
    icon: LayoutDashboard,
  },
  {
    title: "Espacios",
    href: "/spaces",
    icon: FileText,
  },
  {
    title: "Chat IA",
    href: "/chat",
    icon: MessageSquare,
  },
  {
    title: "Categorias",
    href: "/categories",
    icon: FolderKanban,
  },
  {
    title: "Documentos",
    href: "/documents",
    icon: FileStack,
  },
  {
    title: "Manual",
    href: "/manual",
    icon: BookOpen,
  },
];

const adminNavItems: NavItem[] = [
  {
    title: "Usuarios",
    href: "/users",
    icon: Users,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
  {
    title: "Administracion",
    href: "/admin",
    icon: Shield,
    roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"],
  },
];

interface NavLinkProps {
  item: NavItem;
  pathname: string;
  sidebarCollapsed: boolean;
}

const NavLink = React.memo(({ item, pathname, sidebarCollapsed }: NavLinkProps) => {
  // For root path "/", only exact match. For others, also match children routes.
  const isActive = item.href === "/"
    ? pathname === "/"
    : pathname === item.href || pathname.startsWith(`${item.href}/`);
  const Icon = item.icon;

  const linkEl = (
    <Link
      href={item.href}
      className={cn(
        "sidebar-menu__entry__link",
        isActive && "active",
        sidebarCollapsed && "justify-center px-2"
      )}
      aria-label={item.title}
      tabIndex={0}
    >
      <Icon className="sidebar-menu__entry__icon" />
      {!sidebarCollapsed && <span>{item.title}</span>}
    </Link>
  );

  return (
    <div className="sidebar-menu__entry">
      {sidebarCollapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
          <TooltipContent side="right">{item.title}</TooltipContent>
        </Tooltip>
      ) : (
        linkEl
      )}
    </div>
  );
});

NavLink.displayName = "NavLink";

export const Sidebar = () => {
  const pathname = usePathname();
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore();
  const { user } = useAuthStore();

  // Match /spaces/<id>/... — render the space-contextual sidebar.
  const spaceDetailMatch = pathname.match(/^\/spaces\/(\d+)(?:\/.*)?$/);
  if (spaceDetailMatch) {
    const spaceId = Number(spaceDetailMatch[1]);
    if (!isNaN(spaceId)) {
      return <SpaceSidebar spaceId={spaceId} />;
    }
  }

  const userRoles = user?.roles || [];

  const hasAccess = (roles?: string[]) => {
    if (!roles) return true;
    return roles.some((role) => userRoles.includes(role as never));
  };

  return (
    <aside
      className={cn(
        "layout__sidebar sidebar",
        sidebarCollapsed && "layout__sidebar--slim sidebar--slim"
      )}
    >
      {/* Header */}
      <div className="sidebar__section flex items-center justify-between border-b border-[var(--gray-200)]">
        <Link href="/" className="sidebar__brand flex items-center gap-2">
          <Image
            src={BRANDING.LOGO_PATH}
            alt={BRANDING.LOGO_ALT}
            width={BRANDING.LOGO_SIDEBAR_WIDTH}
            height={BRANDING.LOGO_SIDEBAR_HEIGHT}
            priority
            className="object-contain"
          />
          {!sidebarCollapsed && (
            <span className="text-lg font-bold tracking-tight" style={{ color: "var(--gray-900)" }}>
              {BRANDING.APP_NAME}
            </span>
          )}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebarCollapsed}
          className={cn("h-8 w-8", sidebarCollapsed && "mx-auto")}
          aria-label={sidebarCollapsed ? "Expandir menu" : "Colapsar menu"}
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform duration-300",
              sidebarCollapsed && "rotate-180"
            )}
          />
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="sidebar__section--fluid">
        <nav className="sidebar-menu">
          {mainNavItems.map((item) => (
            <NavLink
              key={item.href}
              item={item}
              pathname={pathname}
              sidebarCollapsed={sidebarCollapsed}
            />
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="sidebar__section border-t border-[var(--gray-200)]">
        {adminNavItems.some((item) => hasAccess(item.roles)) && (
          <nav className="sidebar-menu">
            {!sidebarCollapsed && (
              <p className="mb-2 px-4 pt-2 text-xs font-semibold uppercase" style={{ color: "var(--gray-500)" }}>
                Administracion
              </p>
            )}
            {adminNavItems
              .filter((item) => hasAccess(item.roles))
              .map((item) => (
                <NavLink
                  key={item.href}
                  item={item}
                  pathname={pathname}
                  sidebarCollapsed={sidebarCollapsed}
                />
              ))}
          </nav>
        )}
        <div className="sidebar-menu__entry">
          {(() => {
            const settingsLink = (
              <Link
                href="/settings"
                className={cn(
                  "sidebar-menu__entry__link",
                  pathname === "/settings" && "active",
                  sidebarCollapsed && "justify-center px-2"
                )}
                aria-label="Configuracion"
                tabIndex={0}
              >
                <Settings className="sidebar-menu__entry__icon" />
                {!sidebarCollapsed && <span>Configuracion</span>}
              </Link>
            );
            return sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>{settingsLink}</TooltipTrigger>
                <TooltipContent side="right">Configuracion</TooltipContent>
              </Tooltip>
            ) : settingsLink;
          })()}
        </div>
      </div>
    </aside>
  );
};
